import 'react-toastify/dist/ReactToastify.min.css'
import '../styles/globals.css'

import NextApp, { type AppProps, type AppContext } from 'next/app'
import { ToastContainer } from 'react-toastify'

import Providers, { type Branding } from '@/modules/providers'
import { getBranding } from '@/core/static-props/banner'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

if (process.env.NEXT_PUBLIC_API_MOCKING === 'enabled') {
    require('../mocks')
}

import dynamic from 'next/dynamic'
import { pushEvent } from '@/lib/gtm'
import { getCachedStore, getStore } from '@/utils/store'

const ReactQueryDevtools =
    process.env.NODE_ENV === 'development'
        ? dynamic(
              () =>
                  import('@tanstack/react-query-devtools').then(
                      (m) => m.ReactQueryDevtools,
                  ),
              { ssr: false },
          )
        : () => null

const TailwindIndicator =
    process.env.NODE_ENV === 'development'
        ? dynamic(
              () =>
                  import('@/components/atoms/TailwindIndicator/TailwindIndicator'),
              { ssr: false },
          )
        : () => null

function App({ Component, pageProps, branding }: AppProps & { branding: Branding }) {
    const [isRouting, setRouting] = useState(false)
    const router = useRouter()

    useEffect(() => {

        getCachedStore();
        
        const handleRouteChangeStart = () => {
            setRouting(true)
        }
        const handleRouteChangeComplete = (url: string) => {
            setRouting(false)
            pushEvent('page_view', { page_path: url }) // 👈 ajout ici
        }
        router.events.on('routeChangeStart', handleRouteChangeStart)
        router.events.on('hashChangeStart', handleRouteChangeStart)

        router.events.on('routeChangeComplete', handleRouteChangeComplete)
        router.events.on('hashChangeComplete', handleRouteChangeComplete)
        router.events.on('routeChangeError', handleRouteChangeComplete)
        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart)
            router.events.off('routeChangeComplete', handleRouteChangeComplete)
        }
    }, [router.events])

    return (
        <Providers branding={branding}>
            <ToastContainer />
            {process.env.NODE_ENV === 'development' && (
                <>
                    <ReactQueryDevtools buttonPosition="bottom-right" />
                    <TailwindIndicator />
                </>
            )}
            {/* <LoadingOverlay visible={isRouting} /> */}
            <Component {...pageProps} />
        </Providers>
    )
}

App.getInitialProps = async (appContext: AppContext) => {
    const appProps = await NextApp.getInitialProps(appContext)
    const { branding } = await getBranding(getStore())
    return { ...appProps, branding }
}

export default App
